import React, {
  useId,
  useReducer,
  useContext,
  useEffect,
  useState
} from "react"
import type { CSSProperties, ReactNode } from "react"
import set from "lodash.set"
import get from "lodash.get"
import useProgress from "../use-progress"
import useDebounce from "../use-trailing-debounce"

const deepClone = <T,>(obj: T): T => JSON.parse(JSON.stringify(obj))

type FormErrors = Record<string, unknown>
type FormTouched = Record<string, boolean>

type FormStatus = "idle" | "loading" | "success" | "error"

type FormConfig = {
  method?: string
  action?: string
  validation?: boolean | string
}

type RequestOptions = {
  method: string
  data: Record<string, unknown>
}

type RequestFn = (
  action: string,
  options: RequestOptions
) => Promise<Record<string, unknown>>

type ValidationError = {
  field: string
  message: string
}

type FormState<T extends Record<string, unknown> = Record<string, unknown>> = {
  status: FormStatus
  initialData: T
  data: T
  touched: FormTouched
  errors: FormErrors
  submitted: boolean
}

type FormAction<T extends Record<string, unknown> = Record<string, unknown>> =
  | { type: "SET_DATA"; data: T }
  | { type: "SET_VALUE"; name: string; value: unknown }
  | { type: "SET_TOUCHED"; name: string; touched: boolean }
  | { type: "SET_ERROR"; name: string; error: unknown }
  | { type: "SET_ERRORS"; errors: FormErrors }
  | { type: "SUBMIT" }
  | { type: "SET_SUBMITTED" }
  | { type: "RESOLVE" }
  | { type: "REJECT" }
  | { type: "RESET" }

export type UseFormReturn<
  T extends Record<string, unknown> = Record<string, unknown>
> = {
  __configure: React.Dispatch<React.SetStateAction<FormConfig>>
  config: FormConfig
  data: T
  status: FormStatus
  /** Whether the form has been submitted since it was created or reset, whether or not the submission went through */
  submitted: boolean
  reset: () => void
  errors: FormErrors
  submit: () => Promise<{ status: "error" | "success" } | undefined>
  setData: (data: T) => void
  setValue: (name: string, value: unknown) => void
  getValue: (name: string) => unknown
  setTouched: (name: string, touched?: boolean) => void
  getTouched: (name: string) => boolean | undefined
  setError: (name: string, error: unknown) => void
  getError: (name: string) => string | undefined
  setSubmitting: () => void
  setSubmitted: () => void
  setFinished: () => void
}

export type FormConfigProps = {
  request?: RequestFn
  children?: ReactNode
}

export type FormProps = {
  // `any` is intentional — Form doesn't need to know the data shape,
  // it only uses form methods (submit, reset, configure, status).
  form: UseFormReturn<any>
  children?: ReactNode
  method?: string
  action?: string
  onSubmit?: (event: React.FormEvent<HTMLFormElement>) => void
  validation?: boolean | string
  className?: string
  style?: CSSProperties
  id?: string
}

export type UseFieldOptions = {
  name: string
  id?: string
  value?: unknown
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void
}

export type UseFieldReturn = {
  id: string
  value: unknown
  onBlur: (event: React.FocusEvent<HTMLInputElement>) => void
  error: string | undefined
  touched: boolean | undefined
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void
}

const defaultRequest: RequestFn = async (action, { method, data }) => {
  const response = await fetch(action, {
    method: method,
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "X-CSRF-Token": getCSRFToken(),
      // TODO: Remove
      "X-React-RB": "true"
    },
    body: JSON.stringify(data)
  })

  const result = await response.json()

  // TODO: Remove
  if (result.redirect) {
    window.location = result.redirect
  }

  return result
}

const FormContext = React.createContext<UseFormReturn<any> | null>(null)

const FormConfigContext = React.createContext<{ request: RequestFn }>({
  request: defaultRequest
})

export const FormConfig = ({
  request = defaultRequest,
  children
}: FormConfigProps) => {
  return (
    <FormConfigContext.Provider value={{ request }}>
      {children}
    </FormConfigContext.Provider>
  )
}

export function useFormContext() {
  return useContext(FormContext)
}

/**
 * The form's error for a field, once it should show: in a form with
 * `validation`, after the field is touched or the form submitted. Checking
 * `submitted` lets a field the user skipped say why the submission failed.
 */
export function useInlineError(name: string) {
  const form = useFormContext()

  return form?.config?.validation && (form.submitted || form.getTouched(name))
    ? form.getError(name)
    : undefined
}

export function getCSRFToken() {
  try {
    return (document.querySelector("meta[name=csrf-token]") as HTMLMetaElement)
      .content
  } catch (e) {
    return ""
  }
}

export function Form({
  form,
  children,
  method,
  action,
  onSubmit = () => {},
  validation = false,
  className,
  style,
  id
}: FormProps) {
  useEffect(() => {
    form.__configure({ method, action, validation })
  }, [method, action, validation])

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    if (form.status == "loading") {
      event.preventDefault()
      return
    }

    // Before `onSubmit`, so a consumer that handles the submission itself
    // still reveals the errors it sets
    form.setSubmitted()
    onSubmit(event)

    if (event.defaultPrevented) {
      // consumer handled this submission
      return
    }

    event.preventDefault()
    form.submit()
  }

  return (
    <FormContext.Provider value={form}>
      <form
        method={method}
        action={action}
        onSubmit={handleSubmit}
        onReset={form.reset}
        className={className}
        style={style}
        id={id}
      >
        {children}
      </form>
    </FormContext.Provider>
  )
}

function reducer<T extends Record<string, unknown>>(
  state: FormState<T>,
  action: FormAction<T>
): FormState<T> {
  switch (action.type) {
    case "SET_DATA": {
      const { data } = action
      return { ...state, data }
    }
    case "SET_VALUE": {
      const { name, value } = action
      return { ...state, data: { ...set(state.data, name, value) } }
    }
    case "SET_TOUCHED": {
      const { name, touched } = action
      return { ...state, touched: { ...set(state.touched, name, touched) } }
    }
    case "SET_ERROR": {
      const { name, error } = action
      return {
        ...state,
        status: "idle",
        errors: { ...set(state.errors, name, error) }
      }
    }
    case "SET_ERRORS": {
      const { errors } = action
      return { ...state, errors, status: "idle" }
    }
    case "SUBMIT": {
      return { ...state, errors: {}, status: "loading", submitted: true }
    }
    case "SET_SUBMITTED": {
      return { ...state, submitted: true }
    }
    case "RESOLVE": {
      return { ...state, status: "success" }
    }
    case "REJECT": {
      return { ...state, status: "error" }
    }
    case "RESET": {
      return {
        initialData: state.initialData,
        data: deepClone(state.initialData),
        errors: {},
        touched: {},
        submitted: false,
        status: "idle"
      }
    }
    default:
      return state
  }
}

export function useField({
  name,
  id: idProp,
  value: valueProp,
  onChange = () => {},
  onBlur = () => {}
}: UseFieldOptions): UseFieldReturn {
  const form = useFormContext()
  const generatedId = useId()
  const id = idProp ?? generatedId
  // If value is not undefined, the component is controlled.
  const value = valueProp === undefined ? form?.getValue(name) : valueProp

  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    form?.setTouched(name)
    onBlur(event)
  }

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const target = event.target

    const value = target.type === "checkbox" ? target.checked : target.value

    form?.setValue(name, value)
    onChange(event)
  }

  const error = form?.getError(name)
  const touched = form?.getTouched(name)

  return {
    id,
    value,
    onBlur: handleBlur,
    error,
    touched,
    onChange: handleChange
  }
}

function useForm<T extends Record<string, unknown> = Record<string, unknown>>({
  initialData = {} as T
}: { initialData?: T } = {}): UseFormReturn<T> {
  const progress = useProgress()
  const { request } = useContext(FormConfigContext)
  const [config, __configure] = useState<FormConfig>({})
  const [state, dispatch] = useReducer<
    React.Reducer<FormState<T>, FormAction<T>>
  >(reducer, {
    status: "idle",
    initialData,
    data: deepClone(initialData),
    touched: {},
    errors: {},
    submitted: false
  })

  /* Errors */
  const setError = (name: string, error: unknown) => {
    dispatch({ type: "SET_ERROR", name, error })
  }

  const validate = async () => {
    if (config.validation) {
      // If config.validation is true, we're going to use inline validations,
      // but only when the form is submitted.
      if (config.validation !== true) {
        const errors = (await request("/validations", {
          method: "post",
          data: {
            validation_data: state.data,
            validation_type: config.validation
          }
        })) as unknown as ValidationError[]

        dispatch({ type: "SET_ERRORS", errors: {} })

        errors.forEach(error => {
          setError(error.field, error.message)
        })
      }
    }
  }

  const [debouncedValidation] = useDebounce(() => {
    validate()
  }, 700)

  const { data, touched, errors, status, submitted } = state

  /* Values */
  const setValue = (name: string, value: unknown) => {
    debouncedValidation()
    dispatch({ type: "SET_VALUE", name, value })
  }

  const getValue = (name: string) => get(data, name)

  const setData = (data: T) => {
    dispatch({ type: "SET_DATA", data })
  }

  /* Touched */
  const setTouched = (name: string, touched = true) => {
    dispatch({ type: "SET_TOUCHED", name, touched })
  }

  const getTouched = (name: string) =>
    get(touched, name, false) as boolean | undefined

  const reset = () => {
    dispatch({ type: "RESET" })
  }

  const setSubmitting = () => {
    dispatch({ type: "SUBMIT" })
  }

  const setSubmitted = () => {
    dispatch({ type: "SET_SUBMITTED" })
  }

  const setFinished = () => {
    dispatch({ type: "RESOLVE" })
  }

  const submit = async () => {
    if (state.status === "loading") return

    setSubmitting()
    progress?.go(20)

    try {
      const data = await request(config.action!, {
        method: config.method!,
        data: state.data
      })

      if (data.errors) {
        progress?.finish()
        if (config.validation) {
          const validationErrors = data.errors as unknown as ValidationError[]
          validationErrors.forEach(error => {
            setError(error.field, error.message)
          })
        } else {
          dispatch({ type: "SET_ERRORS", errors: data.errors as FormErrors })
        }
        return { status: "error" as const }
      } else {
        setFinished()
        progress?.finish()

        return { status: "success" as const }
      }
    } catch (error) {
      console.error(error)
      progress?.finish()
      dispatch({ type: "REJECT" })
      return { status: "error" as const }
    }
  }

  const getError = (name: string) => get(errors, name) as string | undefined

  return {
    __configure,
    config,
    data,
    status,
    submitted,
    reset,
    errors,
    submit,
    setData,
    setValue,
    getValue,
    setTouched,
    getTouched,
    setError,
    getError,
    setSubmitting,
    setSubmitted,
    setFinished
  }
}

export default useForm
